import { Autocomplete, Stack, StackProps, TextField, Typography } from '@mui/material';
import { compact, debounce, isNil, sortBy, sortedUniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SubscriptionOptionItem } from '@/api/subscriptions';
import { DisplayCurrency } from '@/components/DisplayCurrency';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { TransactionQuantityOptionListItem } from '@/components/transactions/TransactionQuantityOptionListItem';
import { Spacer } from '@/components/util';
import { useTransactionContextLineItemQuantity } from '@/context/transaction';
import { minorTextGrey } from '@/theme';
import { invoiceLabels, isFiniteNumber } from '@/utils';

export type SatCreditsQuantityEditorProps = {
    defaultQuantity?: number;
    defaultQuantityLabel?: string;
    recommendedQuantity?: number;
    item?: SubscriptionOptionItem;
    onChange?: (quantity: number) => void;
} & Pick<StackProps, 'sx'>;

export const SatCreditsQuantityEditor = ({
    defaultQuantity = 500,
    defaultQuantityLabel,
    recommendedQuantity,
    onChange,
    item,
    sx,
}: SatCreditsQuantityEditorProps) => {
    const [initialQuantity, setItemQuantity] = useTransactionContextLineItemQuantity(item?.price_id, defaultQuantity);
    const [open, setOpen] = useState(false);
    const [quantity, setQuantity] = useState<number>();

    const { productName, quantityLabel } = useMemo(() => {
        if (!item?.product_name) {
            return { productName: 'Item', quantityLabel: 'Items' };
        }
        return invoiceLabels(item);
    }, [item]);

    const quantityOptions = useMemo(() => {
        return sortedUniq(
            sortBy(
                [
                    '100',
                    '500',
                    '1000',
                    '5000',
                    ...compact([
                        isFiniteNumber(defaultQuantity) && String(defaultQuantity),
                        isFiniteNumber(recommendedQuantity) && String(recommendedQuantity),
                    ]),
                ],
                Number
            )
        );
    }, [defaultQuantity, recommendedQuantity]);

    const getQuantityLabel = (option: string) => {
        if (defaultQuantityLabel && option === String(defaultQuantity)) {
            return defaultQuantityLabel;
        }
        if (option === String(recommendedQuantity)) {
            return 'Recommended';
        }
        return null;
    };

    const currentValueLabel = useMemo(() => {
        if (recommendedQuantity && quantity === recommendedQuantity) {
            return 'Recommended';
        }
        if (defaultQuantityLabel && quantity === defaultQuantity) {
            return defaultQuantityLabel;
        }
    }, [defaultQuantity, defaultQuantityLabel, quantity, recommendedQuantity]);

    useEffect(() => {
        if (isNil(quantity) && initialQuantity) {
            setQuantity(initialQuantity);
        }
    }, [initialQuantity, quantity]);

    const debouncedSetItemQuantity = useMemo(
        () =>
            debounce((value) => setItemQuantity(value), 150, {
                trailing: true,
            }),

        [setItemQuantity]
    );

    const handleChange = useCallback(
        (_event: unknown, valStr: string) => {
            const value = Number(valStr.replaceAll(/[^\d]/g, ''));
            setQuantity(value);
            debouncedSetItemQuantity(value);
            onChange?.(value);
        },
        [debouncedSetItemQuantity, onChange]
    );

    if (!item) {
        return <GenericSkeleton lines={1} />;
    }

    const { interval, currency, unit_amount: amount } = item;

    return (
        <Stack direction="row" sx={{ alignItems: 'end', ...sx }}>
            <Stack>
                <Typography variant="h5">{productName} base price</Typography>
                <Stack
                    data-testid="sat-credit-editor-price-per-label"
                    direction="row"
                    sx={{ alignItems: 'center', height: '40px' }}
                >
                    <Typography>
                        <DisplayCurrency value={amount} cents currency={currency} sx={{ display: 'inline-block' }} />
                        {interval && ` / ${interval}`}
                    </Typography>
                </Stack>
            </Stack>
            <Spacer />
            <Stack direction="row" sx={{ alignItems: 'center', height: '40px' }}>
                <Typography sx={{ mx: 2 }}>x</Typography>
            </Stack>
            <Stack>
                <Typography variant="h5" sx={{ minWidth: '14em' }}>
                    {quantityLabel}
                </Typography>
                <Autocomplete
                    size="small"
                    value={String(quantity)}
                    options={quantityOptions}
                    filterOptions={(options) => options}
                    includeInputInList
                    freeSolo
                    selectOnFocus
                    forcePopupIcon
                    disableClearable
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    renderOption={({ key, ...props }, option) => (
                        <TransactionQuantityOptionListItem
                            key={key}
                            {...props}
                            label={option}
                            description={getQuantityLabel(option)}
                        />
                    )}
                    renderInput={({ InputProps: { endAdornment, ...InputProps }, ...params }) => (
                        <TextField
                            {...params}
                            data-testid="sat-credit-editor-quantity-input"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <>
                                            {currentValueLabel && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: minorTextGrey, textWrap: 'nowrap' }}
                                                >
                                                    {currentValueLabel}
                                                </Typography>
                                            )}
                                            {endAdornment}
                                        </>
                                    ),
                                    ...InputProps,
                                },
                            }}
                            onKeyDown={(event) => {
                                if (
                                    event.key.length === 1 &&
                                    !(event.ctrlKey || event.altKey || event.metaKey) &&
                                    !event.key.match(/\d/)
                                ) {
                                    event.preventDefault();
                                    return;
                                }
                                if (event.key === 'Enter') {
                                    setOpen(false);
                                }
                            }}
                        />
                    )}
                    onChange={handleChange}
                    onInputChange={handleChange}
                />
            </Stack>
        </Stack>
    );
};
